import api from '@/lib/services/general.service'
import { toast } from 'vue3-toastify';
import { prefetchLogos } from '@/lib/services/logoService'
import { normaliseEmail } from '@/lib/recipientMatch'

/**
 * One entry per PERSON in the contact book (busy-my-web#260).
 *
 * `ADD_CONTACT` used to unshift unconditionally, so a contact created in the box and then handed
 * back by a later fetch/sync — or a get-or-create that answers with the record already in the book —
 * landed twice. #256 hid that at the Add Recipient chooser with `dedupeByEmail`; every other reader
 * of `allContact` still saw both copies.
 *
 * The guard MERGES rather than refuses. The incoming record is the later word and is usually the
 * richer one — a server id and a `timelineId` arriving for a contact that was created locally — and
 * the consumers look a contact up by `id` first (`EmailForm`, `EventForm`, the briefing), so an
 * entry kept without its id is an entry they cannot find. Merging overlays the incoming record's
 * defined fields onto the existing one IN PLACE, so the list keeps its order and a row the user is
 * looking at does not jump.
 */
export function contactKey (contact) {
  return normaliseEmail(contact?.emailAddress)
}

/** The index of the entry that is the same person as `contact`, or -1. */
export function findContactIndex (list = [], contact) {
  const key = contactKey(contact)
  // No address to compare: the id is the only identity left, and a record with neither is new.
  if (!key) {
    if (contact?.id == null) return -1
    return list.findIndex(item => item?.id != null && item.id === contact.id)
  }
  return list.findIndex(item => contactKey(item) === key || (contact.id != null && item?.id === contact.id))
}

/** Existing record widened by the incoming one; `undefined`/`null` incoming fields keep the old value. */
export function mergeContacts (existing, incoming) {
  const merged = { ...(existing || {}) }
  for (const [field, value] of Object.entries(incoming || {})) {
    if (value === undefined || value === null) continue
    merged[field] = value
  }
  return merged
}

/**
 * Adds `contact` to `list`, or merges it into the entry that is already the same person.
 * Answers `true` when the list grew, so the caller can keep a total count honest.
 */
export function upsertContact (list, contact, { prepend = true } = {}) {
  const idx = findContactIndex(list, contact)
  if (idx !== -1) {
    list.splice(idx, 1, mergeContacts(list[idx], contact))
    return false
  }
  if (prepend) list.unshift(contact)
  else list.push(contact)
  return true
}

/** A fetched/synced page folded down to one entry per person, later records merging into earlier. */
export function dedupeContacts (contacts = []) {
  const out = []
  // Anything that is not a list is handed straight back as an empty one: the API answers a shape
  // this store does not recognise more often than it should, and a contact book is not the place
  // to find that out.
  if (!Array.isArray(contacts)) return out
  for (const contact of contacts) upsertContact(out, contact, { prepend: false })
  return out
}


export const state = {
  contact: null,
  contacts: {
    items: [],
    index: 0,
    totalCount: 0,
    totalPages: 0,
    hasPreviousPage: 0,
    hasNextPage: 0,
  },
  isLoading: false ,
  allContact: [],
  error: null,
}
export const getters = {
  contact: s => contactId => { return s.allContact.find(item => item.id === contactId) },
  contactByEmail: s => email => { return s.allContact.find(item => item.emailAddress === email) },
  // contact: s => s.contact,
  contacts: s => s.contacts,
  allContact: s => s.allContact.filter(contact => contact.emailAddress?.trim()),
  allContactsList: s => [...s.allContact].sort((a, b) => (a.name || '').localeCompare(b.name || '')),
  isLoading: s => s.isLoading,
  error: s => s.error,
};
export const actions = {

  async getContact({ commit }, contactId) {
    return await api.getById(`contact`, contactId).then(response => {
      if (response) {
        commit("SET_CONTACT", response.data)
      }
      // The record itself (busy-my-web#240): the briefing store resolves an opened contact through
      // this action the way it resolves a task through task/fetchTaskById. Had no callers before,
      // so nothing relied on the old bare `true`.
      return response?.data ?? null
    })
  },
  async fetchContacts({ commit }, requestData) {
    return await api.get(`contact`, requestData).then(response => {
      if (response) {
        commit("SET_CONTACTS", response.data);
      }
      return true;
    });
  },
  async fetchAllContacts({ commit }) {
    return await api.get(`contact/GetAllContacts`, {}).then(response => {
      if (response) {
        commit("SET_ALL_CONTACTS", response.data);
      }
      return true;
    });
  },
  async AddNewContact({ commit, dispatch }, contact) {
    const silent = !!contact.silentToast
    delete contact.silentToast
    const toastId = silent ? null : toast.loading(
      'Please wait...', {
      theme: 'contact',
    });
    return await api.insert('contact', contact).then(response => {
      if (response) {
        commit("ADD_CONTACT", response.data)
        if (!silent) setTimeout(() => {
          toast.update(toastId, {
            render: "Contact Added",
            autoClose: true,
            closeOnClick: true,
            closeButton: true,
            theme: 'contact',
            isLoading: false,
          });
          toast.done(toastId);
        });
      }
      // The created record, not a bare `true` (busy-my-web#240): the SmartBox needs the new
      // contact's id to hang attachments off it. Every older caller only truth-tested the answer,
      // and a contact object passes the same test.
      return response?.data ?? null;
    });
  },
  /**
   * Get-or-create the contact's timeline (busy-my-web#240). A contact has no timeline until the
   * first document is attached to it — `POST contact/{id}/timeline` (busy-main-bff →
   * acc-account-sync-srv) creates one on demand and answers with the contact now carrying
   * `timelineId`. Idempotent, so calling it for a contact that already has one is a cheap no-op.
   * Returns the contact (or null on failure, so the attach can report itself as failed).
   */
  async ensureContactTimeline({ commit }, contactId) {
    try {
      const response = await api.insert(`contact/${contactId}/timeline`, {});
      const contact = response?.data ?? null;
      if (!contact) return null;
      // The store's copy learns its timelineId, so the next attach skips the round-trip.
      commit('UPDATE_CONTACT', contact);
      return contact;
    } catch (error) {
      console.error('Error ensuring contact timeline:', error);
      return null;
    }
  },
  // `patchModel` is [contactId, jsonPatch, options?] — options.silent skips the toast pair, for
  // callers that report the save themselves (the SmartBox's notice strip, busy-my-web#240).
  async EditContact({ commit }, patchModel) {
    const silent = !!patchModel[2]?.silent
    const toastId = silent ? null : toast.loading(
      'Please wait...', {
      theme: 'contact',
    });
    return await api.patch('contact', patchModel[0], patchModel[1]).then(response => {
      if (response) {
        // A success response isn't guaranteed to carry the updated record (busy-my-web#264) —
        // some callers answer 204/no-body. When it does, the store's copy is replaced with the
        // fresh record as before; when it doesn't, the store is left as-is (the caller already
        // holds the edited fields) rather than handing UPDATE_CONTACT an undefined contact.
        if (response.data) commit("UPDATE_CONTACT", response.data)
        if (!silent) setTimeout(() => {
          toast.update(toastId, {
            render: "Contact Updated",
            autoClose: true,
            closeOnClick: true,
            closeButton: true,
            theme: 'contact',
            isLoading: false,
          });
          toast.done(toastId);
        });
        // The updated record rides back (busy-my-web#240) — same shape AddNewContact answers with.
        return response?.data ?? true;
      }
    });

  },
  async deleteContact({ commit, dispatch }, contactId) {
    const toastId = toast.loading(
      'Please wait...', {
      theme: 'contact',
    });
    return await api.delete(`contact`, contactId)
      .then((response) => {
        if (response) {
          setTimeout(() => {
            toast.update(toastId, {
              render: "Contact Deleted",
              autoClose: true,
              closeOnClick: true,
              closeButton: true,
              theme: 'contact',
              isLoading: false,
            });
            toast.done(toastId);
          });
          commit("REMOVE_CONTACT", contactId);

          return true;
        }
        return false;
      });
  },
  /**
   * Undo for deleteContact (busy-my-web#163). `POST contact/{id}/restore`
   * (busy-main-bff#14 → acc-account-sync-srv 2a3efa9) lifts the tombstone and answers with the
   * contact, so the SAME id and the same children come back — email relations, phone numbers and
   * addresses keep pointing at a contact that still exists, instead of at a row that was replaced.
   * Restoring a contact that is not deleted is a server-side no-op, so a double Undo is harmless.
   */
  async restoreContact({ commit }, contactId) {
    const toastId = toast.loading('Please wait...', { theme: 'contact' });
    try {
      const response = await api.insert(`contact/${contactId}/restore`, {});
      const contact = response?.data ?? null;
      if (!contact) return null;
      commit('ADD_CONTACT', contact);
      setTimeout(() => {
        toast.update(toastId, {
          render: 'Contact restored',
          autoClose: true,
          closeOnClick: true,
          closeButton: true,
          theme: 'contact',
          isLoading: false,
        });
        toast.done(toastId);
      });
      return contact;
    } catch (error) {
      console.error('Error restoring contact:', error);
      // Null so the caller can tell a failed restore from a successful one and fall back to its
      // own snapshot.
      return null;
    }
  },
}
export const mutations = {
  SET_CONTACT(s, contact) {
    s.contact = contact
    s.isLoading = false;
  },
  SET_CONTACTS(s, contacts) {
    // A page can carry the same person twice (busy-my-web#260) — fold it before it reaches the
    // readers. The page keeps its own shape otherwise (index, hasNextPage, …): only `items` is
    // rewritten, and `totalCount` follows it down so the count still describes what is held.
    if (Array.isArray(contacts?.items)) {
      const items = dedupeContacts(contacts.items);
      const dropped = contacts.items.length - items.length;
      s.contacts = { ...contacts, items };
      if (dropped && typeof contacts.totalCount === 'number') {
        s.contacts.totalCount = Math.max(0, contacts.totalCount - dropped);
      }
    } else {
      s.contacts = contacts;
    }
    s.isLoading = false;
  },
  SET_ALL_CONTACTS(s, contacts) {
    const deduped = dedupeContacts(contacts);
    s.allContact = deduped;
    // Prefetch company logos for all contacts in background
    prefetchLogos(deduped);
  },
  ADD_CONTACT(s, contact) {
    if (!contact) return;
    if (upsertContact(s.contacts.items, contact)) s.contacts.totalCount++;
    upsertContact(s.allContact, contact);
  },
  UPDATE_CONTACT(s, contact) {
    const itemIdx = s.contacts.items.findIndex(item => item.id === contact.id)
    if (itemIdx !== -1) s.contacts.items.splice(itemIdx, 1, contact)
    const allIdx = s.allContact.findIndex(item => item.id === contact.id)
    if (allIdx !== -1) s.allContact.splice(allIdx, 1, contact)
  },
  REMOVE_CONTACT(s, id) {
    s.contacts.items = s.contacts.items.filter(item => item.id !== id);
    s.contacts.totalCount--;
    s.allContact = s.allContact.filter(item => item.id !== id);
  },
}
export default {
  state,
  getters,
  actions,
  mutations
};